import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BlockIcon from "@mui/icons-material/Block";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import SubscriptionsOutlinedIcon from "@mui/icons-material/SubscriptionsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";

import {
  Donut, IconBubble, Panel, PanelState, TooltipBox, gridSx, sectionState, useSurface,
} from "../hpc/overview/OverviewUi";
import { PALETTE, compactNumber, formatLkr, longMonth, monthLabel, plural, rangeText } from "../hpc/overview/overviewUtils";
import { MEMBER_STATUS_META } from "../../components/MemberStatusChip";
import { INACTIVE_BUCKET_META, tebaOverviewApi } from "./tebaOverviewApi";

const axisProps = (s) => ({ tick: { fontSize: 12, fill: s.axis }, tickLine: false, axisLine: { stroke: s.grid } });

// ---------------------------------------------------------------- generic pieces
/** First page of a paged TEBA overview list; reloads when ``params`` or ``refreshKey`` change. */
export const useFirstPage = (path, params, refreshKey, enabled = true) => {
  const [result, setResult] = useState({ data: null, error: "" });
  const latest = useRef(0);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) return;
    const id = ++latest.current;
    tebaOverviewApi(path, { ...JSON.parse(key), page: 1, page_size: 10 })
      .then((data) => { if (id === latest.current) setResult({ data, error: "" }); })
      .catch((e) => {
        if (id === latest.current) {
          setResult((r) => ({ data: r.data, error: e?.response?.data?.error || "Could not load this list." }));
        }
      });
  }, [path, key, refreshKey, enabled]);
  return result;
};

function SmallGrid({ rows, columns, onRowClick, getRowId = (r) => r.id }) {
  const s = useSurface();
  return (
    <Box sx={{ height: 360, minWidth: 0 }}>
      <DataGrid rows={rows} columns={columns.map((c) => ({ ...c, sortable: false }))} getRowId={getRowId}
        hideFooter disableRowSelectionOnClick disableColumnMenu onRowClick={onRowClick} rowHeight={40}
        sx={gridSx(s)} />
    </Box>
  );
}

const viewAll = (onClick, disabled) => (
  <Button size="small" endIcon={<ChevronRightIcon />} onClick={onClick} disabled={disabled}
    sx={{ textTransform: "none", fontSize: 13 }}>View all</Button>
);

/** Horizontal bars with label, value and share (members by branch / discipline / subscription, etc.). */
export function BarList({ title, subtitle, Icon, color, section, failed, valueKey = "count", format = (v) => v.toLocaleString(), emptyText, info }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const items = section?.items || [];
  const values = items.map((i) => Number(i[valueKey]) || 0);
  const max = Math.max(...values, 0);
  return (
    <Panel icon={<IconBubble Icon={Icon} color={color} size={36} />} title={title} subtitle={subtitle} info={info}>
      <PanelState state={state} reason={section?.reason} empty={!values.some((v) => v > 0)} height={200}
        emptyText={emptyText || "Nothing to show."}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {items.map((item, index) => (
            <Box key={`${item.key ?? item.label}-${index}`}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontSize: 14, color: s.text }} noWrap>{item.label}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text, whiteSpace: "nowrap" }}>
                  {format(Number(item[valueKey]) || 0)}
                </Typography>
              </Box>
              <Box sx={{ height: 8, borderRadius: 4, bgcolor: s.grid, overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${max ? ((Number(item[valueKey]) || 0) / max) * 100 : 0}%`,
                  bgcolor: color, borderRadius: 4 }} />
              </Box>
            </Box>
          ))}
        </Box>
      </PanelState>
    </Panel>
  );
}

/** Donut over ``section.items`` [{key, <valueKey>}] with labels and colours from ``meta``. */
export function KeyDonut({ title, subtitle, Icon, color, section, failed, meta, valueKey, format, centerLabel, emptyText }) {
  const state = sectionState(section, failed);
  const items = (section?.items || []).map((item) => {
    const value = Number(item[valueKey]) || 0;
    return { key: item.key, label: meta[item.key]?.label || item.label || item.key,
      color: meta[item.key]?.color || PALETTE.grey, value, display: format(value) };
  });
  const total = items.reduce((sum, i) => sum + i.value, 0);
  return (
    <Panel icon={<IconBubble Icon={Icon} color={color} size={36} />} title={title} subtitle={subtitle}>
      <PanelState state={state} reason={section?.reason} empty={!total} height={200} emptyText={emptyText}>
        <Donut items={items} centerValue={format(total)} centerLabel={centerLabel} />
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- membership
export function MemberStatusPanel({ section, failed }) {
  const state = sectionState(section, failed);
  const items = (section?.items || []).map((item) => ({
    key: item.key, label: MEMBER_STATUS_META[item.key]?.label || item.key,
    color: MEMBER_STATUS_META[item.key]?.color || PALETTE.grey, value: item.count, display: item.count.toLocaleString(),
  }));
  return (
    <Panel icon={<IconBubble Icon={DonutLargeOutlinedIcon} color={PALETTE.green} size={36} />}
      title="Membership status" subtitle="Right now" info="Payment Pending: the family's payment has expired. Inactive: set by staff.">
      <PanelState state={state} reason={section?.reason} empty={!section?.total} height={200} emptyText="No members.">
        <Donut items={items} centerValue={(section?.total || 0).toLocaleString()} centerLabel="members" />
      </PanelState>
    </Panel>
  );
}

export function FamiliesPanel({ section, failed }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const figures = [
    { label: "Families", value: (section?.count || 0).toLocaleString() },
    { label: "Average size", value: section?.average_size ?? 0 },
    { label: "Largest family", value: plural(section?.largest || 0, "member") },
  ];
  return (
    <Panel icon={<IconBubble Icon={FamilyRestroomOutlinedIcon} color={PALETTE.teal} size={36} />}
      title="Families" subtitle="Right now">
      <PanelState state={state} reason={section?.reason} empty={!section?.count} height={120} emptyText="No families.">
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {figures.map((f) => (
            <Box key={f.label}>
              <Typography sx={{ fontSize: 13, color: s.muted }}>{f.label}</Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: s.text }}>{f.value}</Typography>
            </Box>
          ))}
        </Box>
      </PanelState>
    </Panel>
  );
}

function AlertRow({ Icon, color, label, value, onClick }) {
  const s = useSurface();
  return (
    <Box onClick={onClick} role={onClick ? "button" : undefined}
      sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1, borderBottom: `1px solid ${s.border}`,
        cursor: onClick ? "pointer" : "default", "&:hover": onClick ? { bgcolor: s.hover } : undefined }}>
      <Icon sx={{ color, fontSize: 20 }} aria-hidden />
      <Typography sx={{ fontSize: 14, color: s.text, flex: 1 }}>{label}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: value ? color : s.muted }}>{value.toLocaleString()}</Typography>
    </Box>
  );
}

export function AlertsPanel({ alerts, failed, onPending }) {
  const state = sectionState(alerts, failed);
  return (
    <Panel icon={<IconBubble Icon={NotificationsActiveOutlinedIcon} color={PALETTE.amber} size={36} />}
      title="Alerts" subtitle="Right now">
      <PanelState state={state} reason={alerts?.reason} height={180}>
        <AlertRow Icon={WarningAmberOutlinedIcon} color={PALETTE.amber} label="Payment pending"
          value={alerts?.payment_pending || 0} onClick={onPending} />
        <AlertRow Icon={BlockIcon} color={PALETTE.grey} label="Inactive (set by staff)" value={alerts?.inactive || 0} />
        <AlertRow Icon={LinkOffOutlinedIcon} color={PALETTE.red} label="Members with no family" value={alerts?.no_family || 0} />
        <AlertRow Icon={SubscriptionsOutlinedIcon} color={PALETTE.red} label="Families with no subscription"
          value={alerts?.no_subscription || 0} />
        <AlertRow Icon={CardMembershipOutlinedIcon} color={PALETTE.teal} label="Free card members"
          value={alerts?.free_card || 0} />
      </PanelState>
    </Panel>
  );
}

export function InactiveBuckets({ section, failed, onBucket }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const rows = [...(section?.buckets || []), { key: "never", count: section?.never_checked_in || 0 }];
  return (
    <Panel icon={<IconBubble Icon={PersonOffOutlinedIcon} color={PALETTE.red} size={36} />}
      title="Not attending" subtitle="Days since the last check-in" info="Members set Inactive by staff are not included.">
      <PanelState state={state} reason={section?.reason} height={180}>
        {rows.map((row) => (
          <Box key={row.key} onClick={() => onBucket(row.key)} role="button"
            sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 1, borderBottom: `1px solid ${s.border}`,
              cursor: "pointer", "&:hover": { bgcolor: s.hover } }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "4px", bgcolor: INACTIVE_BUCKET_META[row.key].color }} />
            <Typography sx={{ fontSize: 14, color: s.text, flex: 1 }}>{INACTIVE_BUCKET_META[row.key].label}</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: s.text }}>{plural(row.count, "member")}</Typography>
            <ChevronRightIcon sx={{ color: s.muted }} />
          </Box>
        ))}
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- lists (first page)
export function FirstPageList({ title, subtitle, Icon, color, path, params, refreshKey, columns, emptyText, onViewAll,
  onRowClick, getRowId, footer, id }) {
  const { data, error } = useFirstPage(path, params, refreshKey);
  const state = data ? sectionState(data, false) : error ? "error" : "loading";
  return (
    <Panel id={id} icon={<IconBubble Icon={Icon} color={color} size={36} />} title={title} subtitle={subtitle}
      action={viewAll(onViewAll, !data?.total_count)}>
      {error && data && <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>{error}</Alert>}
      <PanelState state={state} reason={data?.reason} empty={!data?.items?.length} height={320} emptyText={emptyText}>
        <SmallGrid rows={data?.items || []} columns={columns} onRowClick={onRowClick} getRowId={getRowId} />
        {footer && <Typography sx={{ fontSize: 12.5, mt: 1, color: "text.secondary" }}>{footer(data)}</Typography>}
      </PanelState>
    </Panel>
  );
}

// ---------------------------------------------------------------- finance
export function CollectedChart({ section, failed, range, onMonth }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  const months = (section?.months || []).map((m) => ({ ...m, value: Number(m.amount) || 0 }));
  return (
    <Panel icon={<IconBubble Icon={PaymentsOutlinedIcon} color={PALETTE.purple} size={36} />}
      title="Collected per month" subtitle={`Between ${rangeText(range.from, range.to)} · click a month for its payments`}>
      <PanelState state={state} reason={section?.reason} empty={!months.some((m) => m.value)} height={260}
        emptyText={`No payments between ${rangeText(range.from, range.to)}.`}>
        <Typography sx={{ fontSize: 14, color: s.muted, mb: 1 }}>
          <Box component="span" sx={{ fontWeight: 800, color: s.text }}>{formatLkr(section?.total)}</Box>
          {` from ${plural(section?.payments || 0, "payment")}`}
        </Typography>
        <Box sx={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={months} margin={{ top: 8, right: 8, left: 8, bottom: 4 }} style={{ cursor: "pointer" }}
              onClick={(chart) => {
                const bar = months.find((m) => m.key === chart?.activeLabel);
                if (bar) onMonth(bar);
              }}>
              <CartesianGrid vertical={false} stroke={s.grid} />
              <XAxis dataKey="key" tickFormatter={monthLabel} {...axisProps(s)} />
              <YAxis width={56} tickFormatter={compactNumber} {...axisProps(s)} />
              <ChartTooltip cursor={{ fill: s.hover }} content={({ active, payload, label }) => (active && payload?.length ? (
                <TooltipBox title={longMonth(label)} rows={[
                  { label: "Collected", value: formatLkr(payload[0].payload.value, 2), color: PALETTE.purple },
                  { label: "Payments", value: payload[0].payload.payments },
                ]} />
              ) : null)} />
              <Bar dataKey="value" name="Collected" fill={PALETTE.purple} maxBarSize={40} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </PanelState>
    </Panel>
  );
}

export function OutstandingPanel({ section, failed, onReports }) {
  const s = useSurface();
  const state = sectionState(section, failed);
  return (
    <Panel icon={<IconBubble Icon={WarningAmberOutlinedIcon} color={PALETTE.red} size={36} />}
      title="Outstanding fees" subtitle={`As of today${section?.families ? ` · ${plural(section.families, "family", "families")}` : ""}`}
      info="Same figures as the TEBA outstanding fees report (all disciplines)."
      action={<Button size="small" onClick={onReports} sx={{ textTransform: "none", fontSize: 13 }}>Payment reports</Button>}>
      <PanelState state={state} reason={section?.reason} height={180}>
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: s.text, mb: 1 }}>{formatLkr(section?.total)}</Typography>
        {(section?.by_branch || []).map((row) => (
          <Box key={row.label} sx={{ display: "flex", gap: 1, py: 0.75, borderBottom: `1px solid ${s.border}` }}>
            <Typography sx={{ fontSize: 14, color: s.text, flex: 1 }}>{row.label}</Typography>
            <Typography sx={{ fontSize: 13, color: s.muted }}>{plural(row.families, "family", "families")}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: s.text, minWidth: 110, textAlign: "right" }}>
              {formatLkr(row.amount)}
            </Typography>
          </Box>
        ))}
      </PanelState>
    </Panel>
  );
}
